import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Divider,
  Fade,
  List,
  ListItemButton,
  Paper,
  Slide,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";
import { useState } from "react";

import Logo from "./Logo";
import BWLogo from "./LogoWhite";

import type { ElementType } from "react";

interface TabOption {
  component?: ElementType;
  href?: string;
  label: string;
  to?: string;
}

interface HeaderProps {
  href?: string;
  tabs?: TabOption[];
  title?: string;
  titleLogo?: string;
}

const StyledAppBar = styled(AppBar)(({ theme }) => {
  return {
    alignItems: "center",
    background: "white",
    boxShadow: "0px 10px 15px #35353520",
    display: "flex",
    padding: "0 6rem",

    [theme.breakpoints.down("lg")]: { padding: "0 2rem" },
  };
});

const DivBrand = styled("div")(() => {
  return { alignItems: "center", display: "flex", fontSize: 22 };
});

const DivLogo = styled("div")(({ theme }) => {
  return {
    "& > svg": { width: "100%" },

    alignItems: "center",
    display: "flex",
    [theme.breakpoints.down("xs")]: { width: 100 },

    width: 120,
  };
});

const TypographyTitle = styled(Typography)(({ theme }) => {
  return {
    color: theme.palette.text.primary,

    fontSize: 22,
    fontWeight: 500,
    marginBottom: 2,
    marginLeft: 15,
    [theme.breakpoints.down("xs")]: { fontSize: 15, marginLeft: 10 },
  };
});

const IconButtonMenuBtn = styled(IconButton)(({ theme }) => {
  return {
    "&:hover": { backgroundColor: "inherit" },
    "& svg": { fontSize: 32 },

    boxShadow: "none",

    paddingRight: 0,

    [theme.breakpoints.up("md")]: { display: "none" },
  };
});

const DivFadeWrapper = styled("div")(() => {
  return { position: "fixed", top: 0, zIndex: 1200 };
});

const DivMenuListWrapper = styled("div")(({ theme }) => {
  return {
    backgroundColor: theme.colors.footer,
    height: "100vh",
    width: "100vw",
  };
});

const DivMenuListHeader = styled("div")(() => {
  return {
    alignItems: "center",
    display: "flex",
    height: 68,
    justifyContent: "space-between",
    padding: "0 2rem",
  };
});

const IconButtonCloseBtn = styled(IconButton)(({ theme }) => {
  return {
    "& svg": { fontSize: 32 },

    color: theme.colors.primaryGreen,

    paddingRight: 0,
  };
});

const StyledTabs = styled(Tabs)(({ theme }) => {
  return { [theme.breakpoints.down("md")]: { display: "none" } };
});

const StyledList = styled(List)(() => {
  return { color: "white", padding: "0 2rem", zIndex: 1200 };
});

const StyledListItem = styled(ListItemButton)(() => {
  return {
    "&:hover": { backgroundColor: "inherit" },
    display: "flex",
    fontSize: "1.5rem",
    fontWeight: "bold",

    height: 60,
  };
}) as typeof ListItemButton;

const StyledTab = styled(Tab)(({ theme }) => {
  return {
    "&.Mui-selected": { color: theme.colors.primaryGreen },
    color: theme.colors.primaryBlue,
    fontWeight: 400,
  };
}) as typeof Tab;

const selectInitialTab = (pathValue: string, tabs: TabOption[]) => {
  const newTab = tabs.find((option) => {
    return option.to === `/${pathValue}`;
  });

  // Return route from URL, else return false (no tab selected)
  return newTab ? newTab.label : false;
};

const handleChange = (
  event: React.SyntheticEvent,
  newValue: string,
  tabOptions: TabOption[],
  setValue: (value: false | string) => void,
) => {
  const tab = tabOptions.find((tabOption) => {
    return tabOption.label === newValue;
  });
  if (tab?.to) {
    setValue(newValue);
  }
};

const defaultTabs: TabOption[] = [];

function Header({
  href = "/",
  tabs = defaultTabs,
  title,
  titleLogo,
}: HeaderProps) {
  const [open, setMenuOpen] = useState(false);
  const path =
    typeof window !== "undefined" ? window.location.pathname.split("/")[1] : "";
  const [value, setValue] = useState(selectInitialTab(path, tabs));

  return (
    <>
      <StyledAppBar position="sticky">
        <Link href={href} underline="none">
          <DivBrand>
            <DivLogo>
              <Logo title={titleLogo} />
            </DivLogo>
            {!!title && <TypographyTitle>{title}</TypographyTitle>}
          </DivBrand>
        </Link>

        {/* Show menu button only if there are more than one tab (when the tabs don't fit) */}
        {tabs.length > 1 && (
          <IconButtonMenuBtn
            onClick={() => {
              return setMenuOpen(true);
            }}
          >
            <MenuIcon />
          </IconButtonMenuBtn>
        )}

        {/* Hide tabs when there are more than one (when they don't fit) */}
        <StyledTabs
          onChange={(evt, newValue) => {
            return handleChange(evt, newValue, tabs, setValue);
          }}
          value={value}
          variant="fullWidth"
        >
          {tabs.map((option) => {
            let tabProps = {};
            if (option.href) {
              tabProps = {
                component: "a" as const,
                rel: "noopener",
                target: option.href.startsWith("http") ? "_blank" : undefined,
                ...option,
              };
            } else if (option.component) {
              tabProps = { ...option };
            }
            return (
              <StyledTab
                key={option.label}
                value={option.label}
                {...tabProps}
              />
            );
          })}
        </StyledTabs>
      </StyledAppBar>

      <Paper sx={{ display: { lg: "none", xs: "block" } }}>
        <Slide direction="left" in={open} timeout={{ enter: 400, exit: 800 }}>
          <DivFadeWrapper id="drawer-menu">
            <Fade in={open} timeout={{ enter: 500, exit: 400 }}>
              <DivMenuListWrapper>
                <DivMenuListHeader>
                  <DivLogo>
                    <BWLogo />
                  </DivLogo>
                  <IconButtonCloseBtn
                    onClick={() => {
                      return setMenuOpen(false);
                    }}
                  >
                    <CloseIcon />
                  </IconButtonCloseBtn>
                </DivMenuListHeader>
                <StyledList>
                  {tabs.map((option, idx, arr) => {
                    const { label, ...itemProps } = option || {};
                    const itemButtonProps = option.href
                      ? {
                          component: "a" as const,
                          rel: "noopener",
                          target: "_blank",
                          ...itemProps,
                        }
                      : { ...itemProps };
                    return (
                      <div key={itemButtonProps.to || itemButtonProps.href}>
                        <StyledListItem
                          disableGutters
                          onClick={() => {
                            return setMenuOpen(false);
                          }}
                          {...itemButtonProps}
                        >
                          {label}
                        </StyledListItem>
                        {idx !== arr.length - 1 && (
                          <Divider style={{ backgroundColor: "#707070" }} />
                        )}
                      </div>
                    );
                  })}
                </StyledList>
              </DivMenuListWrapper>
            </Fade>
          </DivFadeWrapper>
        </Slide>
      </Paper>
    </>
  );
}

export default Header;
